/**
 * The chart forms the analytics screens draw with.
 *
 * The rule applied throughout is that the data's job picks the form, not
 * variety for its own sake:
 *
 *   Part-to-whole, few classes, one usually dominant  -> donut
 *   Part-to-whole, very few classes, wants no space   -> share bar
 *   Ranked magnitude with long names                  -> horizontal bars
 *   Change over time                                  -> line
 *
 * That last one is why several panels here are still bars. "Which wording
 * earned the most clicks" is a ranked magnitude comparison with long labels,
 * and a donut of six near-equal slices answers it worse than a bar chart does,
 * however much more varied the page would look.
 *
 * Colour follows the same discipline. Bars encode magnitude by length, so they
 * take one hue — a second hue there would imply a difference that does not
 * exist. Only the part-to-whole forms, where the classes themselves are the
 * subject, use the categorical palette, and they use it in a fixed order so
 * that a filter which removes one class never repaints the others.
 *
 * The palette is the app's own tokens, ordered and stepped so it passes the
 * checks in both themes: worst adjacent CVD ΔE 8.9 light / 8.1 dark, worst
 * normal-vision ΔE 26.9 / 25.6. Three light-mode hues sit under 3:1 against
 * white, so every form here ships visible labels and values rather than
 * relying on the colour to carry identity.
 */
import { useId } from 'react';
import { num, pct } from './TrackBits.jsx';

/** Slots, in fixed order. Never cycled; a ninth class folds into Other. */
const SLOTS = ['u1', 'u2', 'u3', 'u4', 'u5', 'u6'];
const MAX_CLASSES = 6;

/**
 * Keep the classes that carry the story and sum the rest into Other.
 *
 * Past six segments adjacent classes blur, and inventing a seventh hue makes
 * two of them indistinguishable to a colourblind reader. Other is grey on
 * purpose — it is the absence of an identity, not another one.
 */
export function fold(rows, max = MAX_CLASSES) {
  const list = (Array.isArray(rows) ? rows : [])
    .map(r => ({ label: r.label, clicks: Number(r.clicks || 0), uniqueVisitors: Number(r.uniqueVisitors || 0) }))
    .filter(r => r.clicks > 0)
    .sort((a, b) => b.clicks - a.clicks);

  if (list.length <= max) return list;

  const head = list.slice(0, max - 1);
  const tail = list.slice(max - 1);
  head.push({
    label: `Other (${tail.length})`,
    clicks: tail.reduce((n, r) => n + r.clicks, 0),
    uniqueVisitors: tail.reduce((n, r) => n + r.uniqueVisitors, 0),
    other: true
  });
  return head;
}

const slotOf = (row, i) => (row.other ? 'uother' : SLOTS[i % SLOTS.length]);

/**
 * The legend, always present and always carrying the numbers.
 *
 * Identity never rests on colour alone: the name and the value are text, in
 * text colours, with the swatch beside them. That is also what discharges the
 * contrast warning on the lighter hues — the reader is never asked to tell two
 * pale slices apart by eye to know what they are.
 */
function Legend({ rows, total, onPick, picked, inline }) {
  return (
    /* Inline under a full-width bar, stacked beside a donut. A stacked legend
       under a wide bar strands each value at the far right of the card, a
       hand's width from the label it belongs to. */
    <ul className={inline ? 'utlegend utinline' : 'utlegend'}>
      {rows.map((r, i) => (
        <li key={r.label}>
          <button
            type="button"
            className={picked && picked !== r.label ? 'off' : undefined}
            onClick={onPick ? () => onPick(r.label === picked ? null : r.label) : undefined}
            disabled={!onPick}
            title={onPick ? `Show only ${r.label}` : undefined}
          >
            <i className={`utsw ${slotOf(r, i)}`} />
            <span className="utlbl">{r.label}</span>
            <b>{num(r.clicks)}</b>
            <small>{pct(r.clicks, total)}%</small>
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ donut */

/**
 * Part-to-whole at a glance.
 *
 * Segments are arcs on one circle with a gap of surface between them, which is
 * what separates them — not a stroke, which would add ink that is not data.
 * The total sits in the hole, because the question a reader asks of a donut is
 * almost always "out of how many?".
 *
 * Deliberately capped at six and never used where the values are close: a
 * donut cannot support "is this slice bigger than that one" and pretending
 * otherwise is how these become decoration.
 */
export function Donut({ rows, title, note, unit = 'clicks', size = 196 }) {
  const list = fold(rows);
  const total = list.reduce((n, r) => n + r.clicks, 0);
  const clip = useId();

  if (!total) return <p className="muted">No clicks to break down yet.</p>;

  const R = 42;
  const WIDTH = 13;
  const C = 2 * Math.PI * R;
  /* One consistent gap, in the surface colour, between every pair. */
  const GAP = list.length > 1 ? 3 : 0;

  let at = 0;
  const arcs = list.map((r, i) => {
    const share = r.clicks / total;
    const len = Math.max(share * C - GAP, 0.6);
    const arc = { key: r.label, slot: slotOf(r, i), len, offset: -at * C, row: r, share };
    at += share;
    return arc;
  });

  return (
    <div className="utdonut">
      <div className="utring" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" role="img" aria-label={title || 'Breakdown'}>
          <title>{title || 'Breakdown'}</title>
          {arcs.map(a => (
            <circle
              key={a.key}
              className={`utseg ${a.slot}`}
              cx="50" cy="50" r={R}
              strokeWidth={WIDTH}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={a.offset}
              clipPath={`url(#${clip})`}
            >
              <title>{`${a.row.label} — ${num(a.row.clicks)} ${unit}, ${pct(a.row.clicks, total)}%`}</title>
            </circle>
          ))}
        </svg>
        <div className="uthole">
          <b>{num(total)}</b>
          <span>{unit}</span>
        </div>
      </div>
      <div className="utkeys">
        <Legend rows={list} total={total} />
        {note ? <p className="utnote">{note}</p> : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- share bar */

/**
 * The same question as a donut, in one line.
 *
 * Used where there are only two or three classes and a donut would be a large
 * picture of a small fact. Segments carry their own label when the text
 * genuinely fits and fall back to the legend when it does not — a clipped
 * label is worse than no label, so it is measured rather than hoped for.
 */
export function ShareBar({ rows, unit = 'clicks', note }) {
  const list = fold(rows);
  const total = list.reduce((n, r) => n + r.clicks, 0);

  if (!total) return <p className="muted">No clicks to break down yet.</p>;

  return (
    <div className="utshare">
      <div className="uttrack">
        {list.map((r, i) => {
          const share = pct(r.clicks, total);
          return (
            <div
              key={r.label}
              className={`utseg ${slotOf(r, i)}`}
              style={{ flexGrow: r.clicks }}
              title={`${r.label} — ${num(r.clicks)} ${unit}, ${share}%`}
            >
              {/* Roughly six characters need ~9% of the width before the text
                  has room to sit with padding on both sides. */}
              {share >= 14 ? <span>{share}%</span> : null}
            </div>
          );
        })}
      </div>
      <Legend rows={list} total={total} inline />
      {note ? <p className="utnote">{note}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------ ranked bars */

/**
 * Ranked magnitude — the right answer more often than it looks.
 *
 * One hue for every bar, because length already carries the comparison and a
 * second hue would claim a difference in kind that is not there. The value
 * rides the tip of its own bar rather than a number floating on every gridline,
 * and the second figure — unique visitors — stays in the tooltip so the row
 * reads as one number, not two competing ones.
 */
export function RankBars({ rows, unit = 'clicks', limit = 8, note, empty = 'Nothing yet.' }) {
  const list = (Array.isArray(rows) ? rows : [])
    .filter(r => Number(r.clicks) > 0)
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, limit);

  if (!list.length) return <p className="muted">{empty}</p>;

  const top = Math.max(...list.map(r => Number(r.clicks)));

  return (
    <div className="utranks">
      {list.map(r => (
        <div
          className="utrow"
          key={r.label}
          title={`${r.label} — ${num(r.clicks)} ${unit}, ${num(r.uniqueVisitors)} unique`}
        >
          <span className="utname">{r.label}</span>
          <span className="uttrack">
            <i style={{ width: `${Math.max(pct(r.clicks, top), 1.5)}%` }} />
          </span>
          <b>{num(r.clicks)}</b>
        </div>
      ))}
      {note ? <p className="utnote">{note}</p> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- columns */

/**
 * Ranked magnitude as columns standing on a baseline.
 *
 * Reads unmistakably as a chart where a horizontal track can read as a
 * progress bar, and a common baseline is the easiest comparison a reader can
 * be given — the eye judges heights against one line far better than it judges
 * angles or areas.
 *
 * One hue, because the comparison is already carried by height. The value sits
 * on each cap rather than on a gridline, so the chart needs no y-axis at all.
 *
 * A single class is not drawn as a chart. One column against nothing is a
 * number wearing a costume, so it is reported as the sentence it actually is —
 * which is exactly the case a "Cities: Unknown 22" panel presents.
 */
export function Columns({ rows, unit = 'clicks', limit = 7, note, empty = 'Nothing yet.' }) {
  const list = (Array.isArray(rows) ? rows : [])
    .map(r => ({ ...r, clicks: Number(r.clicks || 0) }))
    .filter(r => r.clicks > 0)
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, limit);

  if (!list.length) return <p className="muted">{empty}</p>;

  if (list.length === 1) {
    return (
      <>
        <p className="utonly">
          <b>{num(list[0].clicks)}</b>
          <span>{`${unit}, all of them ${list[0].label}`}</span>
        </p>
        {note ? <p className="utnote">{note}</p> : null}
      </>
    );
  }

  const top = Math.max(...list.map(r => r.clicks));

  return (
    <div className="utcols">
      <div className="utplot">
        {list.map(r => (
          <div
            className="utcol"
            key={r.label}
            title={`${r.label} — ${num(r.clicks)} ${unit}, ${num(r.uniqueVisitors)} unique`}
          >
            <b>{num(r.clicks)}</b>
            {/* A floor of 2% so a class with one click is still a visible mark
                rather than a label floating above nothing. */}
            <i style={{ height: `${Math.max(pct(r.clicks, top), 2)}%` }} />
          </div>
        ))}
      </div>
      <div className="utnames">
        {list.map(r => <span key={r.label} title={r.label}>{r.label}</span>)}
      </div>
      {note ? <p className="utnote">{note}</p> : null}
    </div>
  );
}
